import { FhirStatus, StatusTag, Title } from "@fyrstain/fhir-front-library";
import i18n from "i18next";
import { FunctionComponent } from "react";
import { Accordion, Card, Form } from "react-bootstrap";
import { QRCode } from "react-qrcode";
import { Document, Resource } from "../../services/DocumentService";
import styles from "./BundleDocumentViewer.module.css";

const notAvailable = () => i18n.t("common.notavailable");

function displayConcept(concept: any): string | undefined {
  const coding = concept?.coding?.[0];
  return concept?.text ?? coding?.display ?? coding?.code;
}

function displayQuantity(quantity: any): string | undefined {
  if (quantity?.value === undefined || quantity?.value === null) {
    return undefined;
  }

  return [quantity.value, quantity.unit ?? quantity.code].filter(Boolean).join(" ");
}

function displayDate(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(i18n.language === "fr" ? "fr-FR" : "en-GB", {
    dateStyle: "medium",
  }).format(date);
}

function displayPeriod(period: any): string | undefined {
  const start = displayDate(period?.start);
  const end = displayDate(period?.end);

  return [start, end].filter(Boolean).join(" – ") || undefined;
}

const LabelValueDetail: FunctionComponent<{ label: string; value?: string }> = ({ label, value }) => {
  if (!value) {
    return null;
  }

  return (
    <div className={`d-flex gap-2 ${styles.detail}`}>
      <Form.Label className="fw-semibold">{label}:</Form.Label>
      <Form.Text className={styles.detailValue}>{value}</Form.Text>
    </div>
  );
};

function getStatusColor(status: string): FhirStatus {
  const normalizedStatus = status.toLowerCase();

  if (["stopped", "cancelled", "entered-in-error", "aborted", "refuted", "inactive"].includes(normalizedStatus)) {
    return FhirStatus.error;
  }

  if (["preliminary", "pending", "in-progress", "unknown"].includes(normalizedStatus)) {
    return FhirStatus.pending;
  }

  return FhirStatus.successful;
}

const StatusDetail: FunctionComponent<{ label: string; value?: string }> = ({ label, value }) => {
  if (!value) {
    return null;
  }

  return (
    <div className={`d-flex align-items-center gap-2 ${styles.detail}`}>
      <Form.Label className="mb-0 fw-semibold">{label}:</Form.Label>
      <StatusTag status={getStatusColor(value)} statusMessage={value} />
    </div>
  );
};

const ResourceCard: FunctionComponent<{ resource: Resource }> = ({ resource }) => {
  const data = resource.resource;
  const dosage = data.dosage?.[0];
  const dosageValue = dosage?.text ?? displayQuantity(dosage?.doseAndRate?.[0]?.doseQuantity);

  switch (resource.type) {
    case "MedicationStatement":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.medication")} value={displayConcept(data.medicationCodeableConcept) ?? notAvailable()} />
          <StatusDetail label={i18n.t("label.status")} value={data.status} />
          <LabelValueDetail label={i18n.t("label.effectiveperiod")} value={displayDate(data.effectiveDateTime) ?? displayPeriod(data.effectivePeriod)} />
          <LabelValueDetail label={i18n.t("label.route")} value={displayConcept(dosage?.route)} />
          <LabelValueDetail label={i18n.t("label.dosage")} value={dosageValue} />
        </Card.Body>
      );
    case "Condition":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.pathology")} value={displayConcept(data.code)} />
          <StatusDetail label={i18n.t("label.clinicalstatus")} value={displayConcept(data.clinicalStatus)} />
          <StatusDetail label={i18n.t("label.verificationstatus")} value={displayConcept(data.verificationStatus)} />
          <LabelValueDetail label={i18n.t("label.onsetdate")} value={displayDate(data.onsetDateTime ?? data.onsetPeriod?.start)} />
          <LabelValueDetail label={i18n.t("label.recordeddate")} value={displayDate(data.recordedDate)} />
        </Card.Body>
      );
    case "AllergyIntolerance":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={displayConcept(data.code)} />
          <StatusDetail label={i18n.t("label.clinicalstatus")} value={displayConcept(data.clinicalStatus)} />
          <StatusDetail label={i18n.t("label.verificationstatus")} value={displayConcept(data.verificationStatus)} />
          <LabelValueDetail label={i18n.t("label.criticality")} value={data.criticality} />
          <LabelValueDetail label={i18n.t("label.reaction")} value={displayConcept(data.reaction?.[0]?.manifestation?.[0])} />
          <LabelValueDetail label={i18n.t("label.onsetdate")} value={displayDate(data.onsetDateTime)} />
        </Card.Body>
      );
    case "Procedure":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={displayConcept(data.code)} />
          <StatusDetail label={i18n.t("label.status")} value={data.status} />
          <LabelValueDetail label={i18n.t("label.performeddate")} value={displayDate(data.performedDateTime) ?? displayPeriod(data.performedPeriod)} />
        </Card.Body>
      );
    case "Observation":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={displayConcept(data.code)} />
          <StatusDetail label={i18n.t("label.status")} value={data.status} />
          <LabelValueDetail label={i18n.t("label.value")} value={displayQuantity(data.valueQuantity) ?? data.valueString ?? displayConcept(data.valueCodeableConcept)} />
          <LabelValueDetail label={i18n.t("label.interpretation")} value={displayConcept(data.interpretation?.[0])} />
          <LabelValueDetail label={i18n.t("label.effectiveperiod")} value={displayDate(data.effectiveDateTime) ?? displayPeriod(data.effectivePeriod)} />
        </Card.Body>
      );
    case "DiagnosticReport":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={displayConcept(data.code)} />
          <StatusDetail label={i18n.t("label.status")} value={data.status} />
          <LabelValueDetail label={i18n.t("label.effectiveperiod")} value={displayDate(data.effectiveDateTime) ?? displayPeriod(data.effectivePeriod)} />
          <LabelValueDetail label={i18n.t("label.conclusion")} value={data.conclusion} />
        </Card.Body>
      );
    case "Specimen":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={displayConcept(data.type)} />
          <StatusDetail label={i18n.t("label.status")} value={data.status} />
          <LabelValueDetail label={i18n.t("label.collectiondate")} value={displayDate(data.collection?.collectedDateTime) ?? displayPeriod(data.collection?.collectedPeriod)} />
        </Card.Body>
      );
    case "Immunization":
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={displayConcept(data.vaccineCode)} />
          <StatusDetail label={i18n.t("label.status")} value={data.status} />
          <LabelValueDetail label={i18n.t("label.occurrencedate")} value={displayDate(data.occurrenceDateTime)} />
        </Card.Body>
      );
    default:
      return (
        <Card.Body>
          <LabelValueDetail label={i18n.t("label.type")} value={resource.type} />
        </Card.Body>
      );
  }
};

const BundleDocumentViewer: FunctionComponent<{ document: Document }> = ({ document }) => (
  <>
    <div className="d-flex flex-column gap-4">
      <Card>
        <Card.Header>
          <Title level={2} content={i18n.t("title.information")} />
        </Card.Header>
        <Card.Body className="d-flex flex-column gap-0.5">
          <Title level={3} prefix={i18n.t("title.documentype") + ": "} content={document.type} />
          <Title level={3} prefix={i18n.t("title.documentsubject") + ": "} content={document.subject} />
        </Card.Body>
      </Card>

      <div className="d-flex flex-column gap-3">
        {document.sections?.map((section) => (
          <Accordion key={section.code ?? section.display} defaultActiveKey="none">
            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <Title level={2} content={section.display} />
              </Accordion.Header>
              <Accordion.Body className={styles.scrollable}>
                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xxl-4 g-3">
                  {section.resources.map((resource) => (
                    <div className="col" key={resource.id}>
                      <Card className={styles.card}>
                        <ResourceCard resource={resource} />
                      </Card>
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        ))}
      </div>
    </div>

    <div>
      <QRCode value={document.bundleUrl ?? ""} />
    </div>
  </>
);

export default BundleDocumentViewer;
